import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
    ConsultasOrdenService,
    TipoOrdenConsulta,
} from '@services/orden-consultas/signo-orden-consultas.service';
import { TipoOrdenFormDialogComponent } from '../tipo-orden-form-dialog/tipo-orden-form-dialog.component';
import { SignoSedeService } from '@services/entidades/signo-sedes.service';

// RxJS
import { combineLatest, from } from 'rxjs';
import { map } from 'rxjs/operators';

// Interfaz mínima de sedes
interface Sede {
    _id: string;
    nombre: string;
}

// Tipo de fila para la vista (agrega sedesNombres)
type TipoOrdenRow = TipoOrdenConsulta & { sedesNombres: string[] };

@Component({
    selector: 'app-tipos-orden-consulta',
    templateUrl: './tipos-orden-consulta.component.html',
    styleUrls: ['./tipos-orden-consulta.component.scss'],
})
export class TiposOrdenConsultaComponent implements OnInit {
    displayedColumns = [
        'nombre',
        'descripcion',
        'sedes',
        'ventanas',
        'activo',
        'acciones',
    ];

    // Ahora rows es TipoOrdenRow (tiene sedesNombres ya calculado)
    rows: TipoOrdenRow[] = [];
    loading = true;
    error = '';

    // Para tooltip/estilo opcional si hay sedes desconocidas
    unknownSedeIds = new Set<string>();

    // Diccionario id → nombre
    private sedeNombreById: Record<string, string> = {};

    constructor(
        private srv: ConsultasOrdenService,
        private dialog: MatDialog,
        private signoSedeService: SignoSedeService
    ) {}

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        this.loading = true;
        this.error = '';

        combineLatest([
            this.srv.refreshTiposOrden$(),
            from(this.signoSedeService.getSedes()).pipe(
                map((resp: any) => (resp?.response ?? resp ?? []) as Sede[])
            ),
        ]).subscribe({
            next: ([tipos, sedes]) => {
                // 1) Armar diccionario id → nombre (trim defensivo)
                this.sedeNombreById = (sedes || []).reduce((acc, s) => {
                    const id = this.normalizeSedeId(s?._id);
                    const nombre = (s?.nombre ?? '').toString().trim();
                    if (id) acc[id] = nombre || id;
                    return acc;
                }, {} as Record<string, string>);

                // 2) Detectar ids desconocidos para debug/tooltip
                this.unknownSedeIds.clear();
                (tipos || []).forEach((t) =>
                    (t.sedes || []).forEach((s) => {
                        const key = this.normalizeSedeId(s);
                        if (key && !this.sedeNombreById[key])
                            this.unknownSedeIds.add(key);
                    })
                );

                // 3) Precalcular sedesNombres para la vista
                this.rows = (tipos || []).map((t) => {
                    const sedesNombres = (t.sedes || []).map((s) => {
                        const key = this.normalizeSedeId(s);
                        const nombre = this.sedeNombreById[key] ?? key;
                        return (nombre || '').trim();
                    });
                    return { ...t, sedesNombres };
                });

                this.loading = false;
            },
            error: (e: Error) => {
                this.error = e.message || 'No se pudo cargar';
                this.loading = false;
            },
        });
    }

    /** Normaliza: string | {_id:string} | {$oid:string} → id (string) */
    private normalizeSedeId(s: any): string {
        if (!s) return '';
        if (typeof s === 'string') return s.trim();
        if (typeof s === 'object') {
            if (typeof s._id === 'string') return s._id.trim();
            if (s.$oid && typeof s.$oid === 'string') return s.$oid.trim();
        }
        return String(s).trim();
    }

    formatVentanas(vs: TipoOrdenConsulta['ventanas']): string {
        if (!vs?.length) return '—';
        const dayNames = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
        return vs
            .map((v) => {
                const days = (v.days || [])
                    .map((d) => dayNames[d] ?? String(d))
                    .join(',');
                return `${days} ${v.start}-${v.end}`;
            })
            .join(' | ');
    }

    nuevo(): void {
        if (this.rows?.length && this.rows.length > 0) return; // guarda
        const ref = this.dialog.open(TipoOrdenFormDialogComponent, {
            width: '1000px',
            maxWidth: '98vw',
            maxHeight: '90vh',
            autoFocus: false,
            restoreFocus: true,
            panelClass: 'cip-dialog',
            data: { mode: 'create' as const, item: null },
        });
        ref.afterClosed().subscribe((ok) => ok && this.load());
    }

    editar(item: TipoOrdenConsulta): void {
        const ref = this.dialog.open(TipoOrdenFormDialogComponent, {
            width: '900px', // ancho base cómodo
            maxWidth: '95vw', // IMPORTANT: evita el límite default 80vw
            maxHeight: '90vh', // corta en altura de viewport
            autoFocus: false, // evita scroll forzado al primer control
            restoreFocus: true,
            panelClass: 'cip-dialog', // seguimos usando tu clase
            data: { mode: 'edit' as const, item },
        });
        ref.afterClosed().subscribe((ok) => ok && this.load());
    }
}
